import { useState } from "react";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useReleases } from "../../hooks/useApi";
import { ImpactBadge, SurpriseBadge, MarketViewBadge } from "../../components/Badge";
import { LoadingSpinner, ErrorMessage } from "../../components/LoadingSpinner";
import type { ReleasesFilter } from "../../api/types";

type Country = "US" | "JP" | "";

export function ReleasesPage() {
  const [country, setCountry] = useState<Country>("");
  const [page, setPage] = useState(1);

  const filter: ReleasesFilter = { page, size: 20, country: country || undefined };
  const { data, isLoading, isError } = useReleases(filter);

  const handleCountry = (c: Country) => {
    setCountry(c);
    setPage(1);
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <h1 className="text-2xl font-bold text-gray-900 flex-1">発表履歴</h1>
        <div className="flex gap-2">
          {([["", "すべて"], ["US", "米国"], ["JP", "日本"]] as [Country, string][]).map(
            ([c, label]) => (
              <button
                key={c}
                onClick={() => handleCountry(c)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  country === c
                    ? "bg-blue-600 text-white"
                    : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                }`}
              >
                {label}
              </button>
            )
          )}
        </div>
      </div>

      {data && (
        <div className="text-sm text-gray-500">
          全 {data.total} 件 / {data.pages} ページ中 {data.page} ページ
        </div>
      )}

      {isLoading && <LoadingSpinner />}
      {isError && <ErrorMessage message="発表履歴の取得に失敗しました" />}

      {data && (
        <>
          <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
            {data.items.length === 0 && (
              <p className="py-12 text-center text-gray-500 text-sm">該当データなし</p>
            )}
            {data.items.map((r) => (
              <Link
                key={r.id}
                to={`/releases/${r.id}`}
                className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
              >
                <div className="text-xs text-gray-400 w-28 shrink-0 pt-0.5">
                  <div>{format(parseISO(r.released_at), "yyyy/M/d")}</div>
                  <div>{format(parseISO(r.released_at), "HH:mm")}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold text-gray-400">{r.indicator.country}</span>
                    <span className="font-medium text-gray-900">{r.indicator.name}</span>
                    {r.analysis && <ImpactBadge level={r.analysis.impact_level} />}
                  </div>
                  <div className="text-sm mt-0.5 flex items-center gap-3 flex-wrap">
                    <span className="font-semibold text-gray-800">
                      実績 {r.result}
                      <span className="text-xs text-gray-400 font-normal">{r.indicator.unit}</span>
                    </span>
                    {r.forecast != null && (
                      <span className="text-gray-500 text-xs">予想 {r.forecast}</span>
                    )}
                    {r.previous != null && (
                      <span className="text-gray-500 text-xs">前回 {r.previous}</span>
                    )}
                    {r.surprise_direction && (
                      <SurpriseBadge direction={r.surprise_direction} />
                    )}
                  </div>
                  {r.analysis?.summary && (
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                      {r.analysis.summary}
                    </p>
                  )}
                </div>
                {r.analysis && (
                  <div className="shrink-0">
                    <MarketViewBadge view={r.analysis.market_view} />
                  </div>
                )}
              </Link>
            ))}
          </div>

          <Pagination
            page={data.page}
            pages={data.pages}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => Math.min(data.pages, p + 1))}
          />
        </>
      )}
    </div>
  );
}

function Pagination({
  page,
  pages,
  onPrev,
  onNext,
}: {
  page: number;
  pages: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <div className="flex items-center justify-center gap-3 py-2">
      <button
        onClick={onPrev}
        disabled={page <= 1}
        className="flex items-center gap-1 px-3 py-1.5 rounded border border-gray-200 text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
      >
        <ChevronLeft size={14} /> 前
      </button>
      <span className="text-sm text-gray-600">
        {page} / {pages}
      </span>
      <button
        onClick={onNext}
        disabled={page >= pages}
        className="flex items-center gap-1 px-3 py-1.5 rounded border border-gray-200 text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
      >
        次 <ChevronRight size={14} />
      </button>
    </div>
  );
}
